import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import {
    getDefaultVoiceoverV2GeminiModel,
    getVoiceoverV2FailedInspectionApi,
    getVoiceoverV2InspectionApi,
    processVoiceoverV2FragmentApi,
    VoiceoverV2GeminiModel,
    VoiceoverV2FolderProcessResponse,
    VoiceoverV2FolderResultItem,
    VoiceoverV2FragmentProcessResponse,
    VoiceoverV2InspectionOverviewResponse,
    VoiceoverV2InspectionSegment,
    VoiceoverV2Mode,
} from "api/fastApi";
import {
    LocalStorageKeys,
    MetadataToSubtitlesLanguages,
    SelectedTranslatedTrack,
} from "providers/AudioProvider/types";
import { InputNumber, Modal, Progress, Select, Switch, Table, Tag, Tooltip, message } from "antd";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../../../ManualOnly/AutoEditGate";

const spinKeyframes = `
@keyframes voiceoverV2Spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

type VoiceoverV2LastRunState =
    | {
          type: "fragment";
          fragmentId: string;
          response: VoiceoverV2FragmentProcessResponse;
      }
    | {
          type: "folder";
          response: VoiceoverV2FolderProcessResponse;
      };

type VoiceoverV2FolderProgressState = {
    total: number;
    processed: number;
    passed: number;
    warnings: number;
    errors: number;
    currentFragmentId: string | null;
    stopping: boolean;
    stopped: boolean;
    mode: VoiceoverV2Mode;
    results: VoiceoverV2FolderResultItem[];
};

const STATUS_COLORS: Record<string, string> = {
    passed: "green",
    failed: "red",
    skipped: "gold",
    no_data: "default",
};

const getVoiceoverV2Labels = (useEnglishUi: boolean) => ({
    launchButton: useEnglishUi ? "Set up & run" : "Настроить и запустить",
    modalTitle: useEnglishUi ? "Create Voiceover With Check" : "Создание озвучки с проверкой",
    media: useEnglishUi ? "Media" : "Медиа",
    folder: useEnglishUi ? "Folder" : "Папка",
    notAvailable: useEnglishUi ? "n/a" : "н/д",
    needsContext: useEnglishUi
        ? "Checked voiceover needs media, folder and target language"
        : "Для озвучки с проверкой нужны media, папка и язык перевода",
    loadError: useEnglishUi
        ? "Failed to load checked voiceover data"
        : "Не удалось загрузить данные озвучки с проверкой",
    selectFragment: useEnglishUi ? "Select a fragment first" : "Сначала выберите фрагмент",
    fragmentSuccess: (fragmentId: string) =>
        useEnglishUi
            ? `Checked voiceover fragment ${fragmentId} completed`
            : `Фрагмент ${fragmentId} обработан через озвучку с проверкой`,
    fragmentWarning: (fragmentId: string) =>
        useEnglishUi
            ? `Checked voiceover fragment ${fragmentId} requires review`
            : `Фрагмент ${fragmentId} требует ручной проверки`,
    fragmentInfo: (fragmentId: string) =>
        useEnglishUi
            ? `Checked voiceover fragment ${fragmentId} checked`
            : `Фрагмент ${fragmentId} проверен`,
    fragmentError: useEnglishUi
        ? "Failed to process checked voiceover fragment"
        : "Не удалось обработать фрагмент через озвучку с проверкой",
    folderSuccess: useEnglishUi
        ? "Checked voiceover folder run completed"
        : "Озвучка с проверкой для папки завершена",
    folderError: useEnglishUi
        ? "Failed to process checked voiceover folder"
        : "Не удалось обработать папку через озвучку с проверкой",
    folderStop: useEnglishUi ? "Stop folder run" : "Остановить запуск папки",
    folderStopping: useEnglishUi ? "Stopping after current fragment..." : "Остановка после текущего фрагмента...",
    folderStopped: useEnglishUi ? "Folder run stopped" : "Запуск папки остановлен",
    folderPreparing: useEnglishUi ? "Preparing folder run..." : "Подготовка запуска папки...",
    folderProcessing: useEnglishUi ? "Processing fragments..." : "Обработка фрагментов...",
    folderCompleted: useEnglishUi ? "Folder run completed" : "Запуск папки завершён",
    folderCurrentFragment: useEnglishUi ? "Current fragment" : "Текущий фрагмент",
    folderProgress: useEnglishUi ? "Progress" : "Прогресс",
    folderNoSegments: useEnglishUi
        ? "No fragments found for the current folder"
        : "Для текущей папки не найдены фрагменты",
    source: useEnglishUi ? "Source" : "Исходный язык",
    target: useEnglishUi ? "Target" : "Язык перевода",
    onlyFailed: useEnglishUi ? "Only failed" : "Только ошибки",
    useForNote: useEnglishUi ? "Use for note icon" : "Использовать для иконки ноты",
    useForNoteHint: useEnglishUi
        ? "When enabled, the note icon creates voiceover with check instead of regular synthesis."
        : "Если включено, иконка ноты запускает озвучку с проверкой вместо обычного синтеза.",
    mode: useEnglishUi ? "Mode" : "Режим",
    retryCount: useEnglishUi ? "Retry count" : "Число попыток",
    inspectCurrentFolder: useEnglishUi ? "Inspect current folder" : "Проверить текущую папку",
    inspectFailed: useEnglishUi ? "Inspect failed" : "Показать ошибки",
    runCurrentFolder: useEnglishUi ? "Run current folder" : "Запустить текущую папку",
    runFolderWithRegenerate: useEnglishUi
        ? "Run folder with regenerate"
        : "Запустить папку с пересозданием",
    regenerateConfirmTitle: useEnglishUi
        ? "Regenerate the whole folder?"
        : "Пересоздать всю папку?",
    regenerateConfirmText: useEnglishUi
        ? "This will force-regenerate checked voiceover for the whole current folder. Continue?"
        : "Это принудительно пересоздаст озвучку с проверкой для всей текущей папки. Продолжить?",
    confirmRun: useEnglishUi ? "Run" : "Запустить",
    cancel: useEnglishUi ? "Cancel" : "Отмена",
    runSelectedFragment: useEnglishUi ? "Run selected fragment" : "Запустить выбранный фрагмент",
    selectedFragment: (fragmentId: string) =>
        useEnglishUi ? `Selected: ${fragmentId}` : `Выбрано: ${fragmentId}`,
    noFragmentSelected: useEnglishUi ? "No fragment selected" : "Фрагмент не выбран",
    selectedFragmentTooltip: (fragmentId: string) =>
        useEnglishUi ? `Selected fragment: ${fragmentId}` : `Выбранный фрагмент: ${fragmentId}`,
    unselectedFragmentTooltip: useEnglishUi
        ? "Select a translated row or source row first"
        : "Сначала выберите строку перевода или оригинала",
    lastFragmentRun: useEnglishUi ? "Last fragment run:" : "Последний запуск фрагмента:",
    lastFolderRun: useEnglishUi ? "Last folder run:" : "Последний запуск папки:",
    total: useEnglishUi ? "total" : "всего",
    passed: useEnglishUi ? "passed" : "успешно",
    warnings: useEnglishUi ? "warnings" : "предупреждения",
    errors: useEnglishUi ? "errors" : "ошибки",
    quality: useEnglishUi ? "quality" : "качество",
    intonation: useEnglishUi ? "intonation" : "интонация",
    status: useEnglishUi ? "Status" : "Статус",
    chunk: useEnglishUi ? "Chunk" : "Фрагмент",
    score: useEnglishUi ? "Score" : "Оценка",
    attemptsFails: useEnglishUi ? "Attempts/Fails" : "Попытки/ошибки",
    manualReview: useEnglishUi ? "Manual Review" : "Ручная проверка",
    captions: useEnglishUi ? "Captions" : "Текст",
    reason: useEnglishUi ? "Reason" : "Причина",
    action: useEnglishUi ? "Action" : "Действие",
    runFragment: useEnglishUi ? "Run fragment" : "Запустить фрагмент",
    reviewRequired: useEnglishUi ? "required" : "нужна",
    reviewNo: useEnglishUi ? "no" : "нет",
    noFailedData: useEnglishUi
        ? "No failed checked-voiceover segments for this folder"
        : "Для этой папки нет проблемных фрагментов озвучки с проверкой",
    noInspectionData: useEnglishUi
        ? "No checked-voiceover inspection data for this folder"
        : "Для этой папки нет данных проверки озвучки",
    yes: useEnglishUi ? "yes" : "да",
    no: useEnglishUi ? "no" : "нет",
    statusPassed: useEnglishUi ? "passed" : "успешно",
    statusFailed: useEnglishUi ? "failed" : "ошибка",
    statusSkipped: useEnglishUi ? "skipped" : "пропущено",
    statusWarning: useEnglishUi ? "warning" : "предупреждение",
    statusError: useEnglishUi ? "error" : "ошибка",
    statusOk: useEnglishUi ? "ok" : "успешно",
    statusNoData: useEnglishUi ? "no data" : "нет данных",
    reviewSuffix: useEnglishUi ? "review" : "проверка",
});

type VoiceoverV2Labels = ReturnType<typeof getVoiceoverV2Labels>;

const getReadableModeOptions = (useEnglishUi: boolean): { value: VoiceoverV2Mode; label: string }[] => [
    {
        value: "check_only",
        label: useEnglishUi ? "Check only" : "Только проверка",
    },
    {
        value: "autofix",
        label: useEnglishUi ? "Auto-fix" : "Автоисправление",
    },
    {
        value: "force_regenerate",
        label: useEnglishUi ? "Force regenerate" : "Пересоздать заново",
    },
];

const getGeminiModelOptions = (
    useEnglishUi: boolean,
): { value: VoiceoverV2GeminiModel; label: string }[] => [
    {
        value: "gemini-3-pro-preview",
        label: useEnglishUi ? "Gemini 3 Pro Preview (default)" : "Gemini 3 Pro Preview (по умолчанию)",
    },
    {
        value: "gemini-3.1-pro-preview",
        label: "gemini-3.1-pro-preview",
    },
    {
        value: "gemini-3.1-flash-lite-preview",
        label: "gemini-3.1-flash-lite-preview",
    },
    {
        value: "gemini-3.1-flash-live-preview",
        label: "gemini-3.1-flash-live-preview",
    },
    {
        value: "gemini-3-flash-preview",
        label: "gemini-3-flash-preview",
    },
];

export const normalizeVoiceoverV2Lang = (lang?: string | null) => {
    const normalized = String(lang || "")
        .trim()
        .toLowerCase()
        .split("-", 1)[0];

    if (normalized === "zh") {
        return "ch";
    }

    return normalized;
};

export const getSelectedVoiceoverV2FragmentId = ({
    translatedTracks,
    originalTracks,
}: {
    translatedTracks?: SelectedTranslatedTrack[];
    originalTracks?: SelectedTranslatedTrack[];
}) => {
    const translatedTrack = translatedTracks?.[0]?.track;
    if (translatedTrack) {
        return translatedTrack.original_id || translatedTrack.id || null;
    }

    const originalTrack = originalTracks?.[0]?.track;
    if (originalTrack) {
        return originalTrack.original_id || originalTrack.id || null;
    }

    return null;
};

export const flattenVoiceoverV2InspectionSegments = ({
    overview,
    folderNum,
    targetLang,
}: {
    overview: VoiceoverV2InspectionOverviewResponse | null;
    folderNum: number | null;
    targetLang: string;
}) => {
    if (!overview || folderNum == null || !targetLang) {
        return [] as VoiceoverV2InspectionSegment[];
    }

    const folderData = overview.segments_by_folder[String(folderNum)] || {};
    const rows = folderData[targetLang] || [];

    return [...rows].sort((left, right) => Number(left.start || 0) - Number(right.start || 0));
};

/**
 * Прогон ПАПКИ, который меняет её озвучку: «автоисправление» переозвучивает не прошедшие проверку
 * реплики, «пересоздать» — все. «Только проверка» лишь пишет отметку о проверке.
 * В режиме «Без автоправок» такой прогон выключен; одна выбранная реплика — просьба человека,
 * она работает. Соседей сервер ручной пары после неё трогает только так: озвучки, на которые
 * налез новый дубль, отодвигает позже (правило владельца 01.10.2026) и называет каждую в
 * `pushed` ответа; раздвижка редактора (cascadeNeighbours) — лишь запасной путь для старого
 * сервера без `pushed` или с неудавшимся проходом (`overlap_push_failed`).
 */
export const voiceoverV2FolderRunEditsVoicing = (mode: VoiceoverV2Mode) => mode !== "check_only";

export const summarizeVoiceoverV2FolderResult = (
    response: VoiceoverV2FolderProcessResponse | null,
) => {
    const items = response?.results || [];
    const passed = items.filter((item) => item.result.passed).length;
    const warnings = items.filter(
        (item) => item.result.status === "warning" || (!item.result.passed && item.result.status === "ok"),
    ).length;
    const errors = items.filter((item) => item.result.status === "error").length;
    const problematic = items.filter((item) => !item.result.passed || item.result.status !== "ok");

    return {
        total: items.length,
        passed,
        warnings,
        errors,
        problematic,
    };
};

const buildVoiceoverV2FolderResponse = (
    results: VoiceoverV2FolderResultItem[],
): VoiceoverV2FolderProcessResponse => ({
    status: results.some((item) => item.result.status === "error") ? "warning" : "ok",
    results,
});

const formatVoiceoverV2Status = (status: string | null | undefined, labels: VoiceoverV2Labels) => {
    switch (status) {
        case "passed":
            return labels.statusPassed;
        case "failed":
            return labels.statusFailed;
        case "skipped":
            return labels.statusSkipped;
        case "warning":
            return labels.statusWarning;
        case "error":
            return labels.statusError;
        case "ok":
            return labels.statusOk;
        case "no_data":
            return labels.statusNoData;
        default:
            return status || labels.notAvailable;
    }
};

const getCheckSummary = (
    result: { passed?: boolean; skipped?: boolean; score?: number | null } | undefined,
    labels: VoiceoverV2Labels,
) => {
    if (!result) {
        return labels.notAvailable;
    }
    if (result.skipped) {
        return labels.statusSkipped;
    }

    const state = result.passed ? labels.statusPassed : labels.statusFailed;
    return result.score != null ? `${state} (${result.score})` : state;
};

export const VoiceoverV2Debug: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [showModal, setShowModal] = useState(false);
    const [inspectionLoading, setInspectionLoading] = useState(false);
    const [runLoading, setRunLoading] = useState<null | "fragment" | "folder">(null);
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [mode, setMode] = useState<VoiceoverV2Mode>("check_only");
    const [geminiModel, setGeminiModel] = useState<VoiceoverV2GeminiModel>(
        getDefaultVoiceoverV2GeminiModel(),
    );
    const [retryCount, setRetryCount] = useState(() => {
        const saved = localStorage.getItem(LocalStorageKeys.voiceoverV2RetryCount);
        const parsed = saved ? Number(saved) : NaN;
        return Number.isFinite(parsed) && parsed >= 1 ? parsed : 5;
    });
    const [inspectionOverview, setInspectionOverview] =
        useState<VoiceoverV2InspectionOverviewResponse | null>(null);
    const [lastRun, setLastRun] = useState<VoiceoverV2LastRunState | null>(null);
    const [folderProgress, setFolderProgress] = useState<VoiceoverV2FolderProgressState | null>(null);
    const folderRunAbortRef = useRef<AbortController | null>(null);
    const stopFolderRunRef = useRef(false);
    const autoEdit = useAutoEditBlock();

    const mediaId = state.mediaId;
    const folderNum = state.folder != null ? parseInt(String(state.folder), 10) : null;
    const languageFrom = MetadataToSubtitlesLanguages[state.originalLang];
    const languageTo = state.lang;
    const useEnglishUi = useIsEnglishUi();
    const isDebugMode =
        typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debug");
    const labels = useMemo(() => getVoiceoverV2Labels(useEnglishUi), [useEnglishUi]);
    const modeOptions = useMemo(() => getReadableModeOptions(useEnglishUi), [useEnglishUi]);
    const geminiModelOptions = useMemo(() => getGeminiModelOptions(useEnglishUi), [useEnglishUi]);
    const geminiModelLabel = useMemo(
        () => (useEnglishUi ? "Gemini model" : "Gemini модель"),
        [useEnglishUi],
    );
    const noteToggleLabel = useMemo(
        () =>
            useEnglishUi
                ? "Use Auto-fix for note icon"
                : "Использовать Auto-fix для иконки ноты",
        [useEnglishUi],
    );
    const noteToggleHint = useMemo(
        () =>
            useEnglishUi
                ? "When enabled, the note icon runs checked voiceover in Auto-fix mode instead of regular synthesis."
                : "Если включено, иконка ноты запускает озвучку с проверкой в режиме Auto-fix вместо обычного синтеза.",
        [useEnglishUi],
    );
    const normalizedLanguageTo = normalizeVoiceoverV2Lang(languageTo);
    const selectedFragmentId = useMemo(
        () =>
            getSelectedVoiceoverV2FragmentId({
                translatedTracks: state.selectedTranslatedTrackIds,
                originalTracks: state.selectedOriginalTrackIds,
            }),
        [state.selectedTranslatedTrackIds, state.selectedOriginalTrackIds],
    );

    const inspectionRows = useMemo(
        () =>
            flattenVoiceoverV2InspectionSegments({
                overview: inspectionOverview,
                folderNum,
                targetLang: normalizedLanguageTo,
            }),
        [inspectionOverview, folderNum, normalizedLanguageTo],
    );

    const loadInspection = useCallback(
        async (failedOnly: boolean) => {
            if (!mediaId || folderNum == null || !languageTo) {
                message.error(labels.needsContext);
                return;
            }

            setInspectionLoading(true);
            try {
                const api = failedOnly
                    ? getVoiceoverV2FailedInspectionApi
                    : getVoiceoverV2InspectionApi;
                const response = await api({
                    mediaId,
                    folderNum,
                    languageFrom,
                    languageTo,
                });

                setInspectionOverview(response);
                setOnlyFailed(failedOnly);
            } catch (error) {
                console.error("Voiceover V2 inspection error:", error);
                message.error(
                    error instanceof Error ? error.message : labels.loadError,
                );
            } finally {
                setInspectionLoading(false);
            }
        },
        [folderNum, languageFrom, languageTo, labels.loadError, labels.needsContext, mediaId],
    );

    useEffect(() => {
        if (!showModal) {
            return;
        }

        void loadInspection(false);
    }, [loadInspection, showModal]);

    useEffect(() => {
        return () => {
            stopFolderRunRef.current = true;
            folderRunAbortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        localStorage.setItem(LocalStorageKeys.voiceoverV2RetryCount, String(retryCount));
    }, [retryCount]);

    const handleOpen = () => {
        setOnlyFailed(false);
        setLastRun(null);
        setFolderProgress(null);
        setInspectionOverview(null);
        setShowModal(true);
    };

    const handleRunFragment = async (fragmentId: string | null) => {
        if (!fragmentId || !mediaId || folderNum == null || !languageTo) {
            message.error(labels.selectFragment);
            return;
        }

        setRunLoading("fragment");
        try {
            const response = await processVoiceoverV2FragmentApi({
                mediaId,
                folderNum,
                languageFrom,
                languageTo,
                metasubtitleId: fragmentId,
                retryCount,
                mode,
                geminiModel,
            });

            setLastRun({
                type: "fragment",
                fragmentId,
                response,
            });
            await handles.refreshAllSubtitles();
            await loadInspection(onlyFailed);

            if (response.passed) {
                message.success(labels.fragmentSuccess(fragmentId));
            } else if (response.status === "warning") {
                message.warning(labels.fragmentWarning(fragmentId));
            } else {
                message.info(labels.fragmentInfo(fragmentId));
            }
        } catch (error) {
            console.error("Voiceover V2 fragment error:", error);
            message.error(
                error instanceof Error ? error.message : labels.fragmentError,
            );
        } finally {
            setRunLoading(null);
        }
    };

    const handleRunFolder = async (requestedMode: VoiceoverV2Mode = mode) => {
        if (autoEdit.blocked && voiceoverV2FolderRunEditsVoicing(requestedMode)) return;
        if (!mediaId || folderNum == null || !languageTo) {
            message.error(labels.needsContext);
            return;
        }

        setRunLoading("folder");
        setFolderProgress(null);
        setLastRun(null);
        stopFolderRunRef.current = false;
        const abortController = new AbortController();
        folderRunAbortRef.current = abortController;

        try {
            setFolderProgress({
                total: 0,
                processed: 0,
                passed: 0,
                warnings: 0,
                errors: 0,
                currentFragmentId: null,
                stopping: false,
                stopped: false,
                mode: requestedMode,
                results: [],
            });

            const overview = await getVoiceoverV2InspectionApi({
                mediaId,
                folderNum,
                languageFrom,
                languageTo,
            });
            const rows = flattenVoiceoverV2InspectionSegments({
                overview,
                folderNum,
                targetLang: normalizedLanguageTo,
            });

            if (!rows.length) {
                setFolderProgress(null);
                message.info(labels.folderNoSegments);
                return;
            }

            setFolderProgress({
                total: rows.length,
                processed: 0,
                passed: 0,
                warnings: 0,
                errors: 0,
                currentFragmentId: null,
                stopping: false,
                stopped: false,
                mode: requestedMode,
                results: [],
            });

            const results: VoiceoverV2FolderResultItem[] = [];

            for (const row of rows) {
                if (stopFolderRunRef.current) {
                    break;
                }

                setFolderProgress((prev) =>
                    prev
                        ? {
                              ...prev,
                              currentFragmentId: row.chunk_id,
                          }
                        : prev,
                );

                try {
                    const result = await processVoiceoverV2FragmentApi({
                        mediaId,
                        folderNum,
                        languageFrom,
                        languageTo,
                        metasubtitleId: row.chunk_id,
                        retryCount,
                        mode: requestedMode,
                        signal: abortController.signal,
                        geminiModel,
                    });
                    results.push({ id: row.chunk_id, result });
                } catch (error) {
                    if (abortController.signal.aborted || stopFolderRunRef.current) {
                        break;
                    }

                    const fallbackMessage =
                        error instanceof Error ? error.message : labels.fragmentError;
                    results.push({
                        id: row.chunk_id,
                        result: {
                            status: "error",
                            passed: false,
                            last_error: fallbackMessage,
                        },
                    });
                }

                setFolderProgress((prev) => {
                    if (!prev) {
                        return prev;
                    }

                    const current = results[results.length - 1];
                    if (!current) {
                        return prev;
                    }

                    const isError = current.result.status === "error";
                    const isWarning =
                        current.result.status === "warning" ||
                        (!current.result.passed && current.result.status === "ok");

                    return {
                        ...prev,
                        processed: results.length,
                        passed: prev.passed + (current.result.passed ? 1 : 0),
                        warnings: prev.warnings + (isWarning ? 1 : 0),
                        errors: prev.errors + (isError ? 1 : 0),
                        currentFragmentId: null,
                        results: [...results],
                    };
                });
            }

            const stopped = stopFolderRunRef.current;
            const response = buildVoiceoverV2FolderResponse(results);

            setLastRun({
                type: "folder",
                response,
            });
            setFolderProgress((prev) =>
                prev
                    ? {
                          ...prev,
                          processed: results.length,
                          currentFragmentId: null,
                          results: [...results],
                          stopped,
                          stopping: false,
                      }
                    : prev,
            );
            await handles.refreshAllSubtitles();
            await loadInspection(onlyFailed);

            if (stopped) {
                message.warning(labels.folderStopped);
            } else {
                message.success(labels.folderSuccess);
            }
        } catch (error) {
            if (!abortController.signal.aborted) {
                console.error("Voiceover V2 folder error:", error);
                message.error(
                    error instanceof Error ? error.message : labels.folderError,
                );
            }
        } finally {
            folderRunAbortRef.current = null;
            stopFolderRunRef.current = false;
            setRunLoading(null);
        }
    };

    const handleStopFolderRun = () => {
        if (runLoading !== "folder" || stopFolderRunRef.current) {
            return;
        }

        stopFolderRunRef.current = true;
        folderRunAbortRef.current?.abort();
        setFolderProgress((prev) =>
            prev
                ? {
                      ...prev,
                      stopping: true,
                  }
                : prev,
        );
    };

    const handleRunFolderWithRegenerate = () => {
        if (autoEdit.blocked) return;
        Modal.confirm({
            title: labels.regenerateConfirmTitle,
            content: labels.regenerateConfirmText,
            okText: labels.confirmRun,
            cancelText: labels.cancel,
            okButtonProps: { danger: true },
            onOk: async () => {
                if (runLoading) {
                    return;
                }
                await handleRunFolder("force_regenerate");
            },
        });
    };

    const folderSummary = useMemo(() => {
        if (lastRun?.type !== "folder") {
            return null;
        }
        return summarizeVoiceoverV2FolderResult(lastRun.response);
    }, [lastRun]);

    const folderProgressPercent = useMemo(() => {
        if (!folderProgress?.total) {
            return 0;
        }
        return Math.round((folderProgress.processed / folderProgress.total) * 100);
    }, [folderProgress]);

    const columns = [
        {
            title: labels.status,
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (value: string, record: VoiceoverV2InspectionSegment) => (
                <Tag color={STATUS_COLORS[value] || "default"}>
                    {record.requires_manual_review
                        ? `${formatVoiceoverV2Status(value, labels)} / ${labels.reviewSuffix}`
                        : formatVoiceoverV2Status(value, labels)}
                </Tag>
            ),
        },
        {
            title: labels.chunk,
            dataIndex: "chunk_id",
            key: "chunk_id",
            width: 110,
        },
        {
            title: labels.score,
            dataIndex: "score",
            key: "score",
            width: 80,
            render: (value: number | null) => value ?? labels.notAvailable,
        },
        {
            title: labels.attemptsFails,
            key: "attempts",
            width: 130,
            render: (_: unknown, record: VoiceoverV2InspectionSegment) =>
                `${record.attempts_made || 0}/${record.fail_count || 0}`,
        },
        {
            title: labels.manualReview,
            dataIndex: "requires_manual_review",
            key: "requires_manual_review",
            width: 130,
            render: (value: boolean) => (
                value ? <Tag color="orange">{labels.reviewRequired}</Tag> : <Tag>{labels.reviewNo}</Tag>
            ),
        },
        {
            title: labels.captions,
            dataIndex: "captions",
            key: "captions",
            ellipsis: true,
        },
        {
            title: labels.reason,
            dataIndex: "reason",
            key: "reason",
            ellipsis: true,
        },
        {
            title: labels.action,
            key: "action",
            width: 120,
            render: (_: unknown, record: VoiceoverV2InspectionSegment) => (
                <ButtonH
                    sm
                    ghost
                    onClick={() => {
                        if (runLoading) {
                            return;
                        }
                        void handleRunFragment(record.chunk_id);
                    }}
                >
                    {labels.runFragment}
                </ButtonH>
            ),
        },
    ];

    const selectedFragmentTooltip = selectedFragmentId
        ? labels.selectedFragmentTooltip(selectedFragmentId)
        : labels.unselectedFragmentTooltip;

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 6 }}>
                <ButtonH sm ghost onClick={handleOpen}>
                    <Ico size={"16"}>record_voice_over</Ico>
                    {labels.launchButton}
                </ButtonH>
                <Tooltip title={noteToggleHint}>
                    <div className="_f _fm" style={{ gap: 8 }}>
                        <Switch
                            checked={state.useCheckedVoiceoverSynthesis}
                            onChange={(checked) => handles.setUseCheckedVoiceoverSynthesis(checked)}
                        />
                        <span style={{ fontSize: 12 }}>{noteToggleLabel}</span>
                    </div>
                </Tooltip>
            </div>

            <Modal
                title={labels.modalTitle}
                open={showModal}
                onCancel={() => {
                    if (runLoading === "folder") {
                        handleStopFolderRun();
                    }
                    setShowModal(false);
                }}
                footer={null}
                width={1180}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
                            gap: 8,
                            alignItems: "end",
                        }}
                    >
                        <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{labels.media}</div>
                            <div>{mediaId || labels.notAvailable}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{labels.folder}</div>
                            <div>{folderNum ?? labels.notAvailable}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{labels.source}</div>
                            <div>{languageFrom || labels.notAvailable}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{labels.target}</div>
                            <div>{languageTo || labels.notAvailable}</div>
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                alignItems: "flex-end",
                            }}
                        >
                            <div style={{ flex: "0 1 220px", minWidth: 180 }}>
                                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{labels.mode}</div>
                                <Select
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                    style={{ width: "100%" }}
                                />
                            </div>
                            {isDebugMode ? (
                                <div style={{ flex: "1 1 320px", minWidth: 240 }}>
                                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                                        {geminiModelLabel}
                                    </div>
                                    <Select
                                        value={geminiModel}
                                        onChange={setGeminiModel}
                                        options={geminiModelOptions}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            ) : null}
                            <div style={{ flex: "0 1 150px", minWidth: 120 }}>
                                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{labels.retryCount}</div>
                                <InputNumber
                                    min={1}
                                    max={20}
                                    value={retryCount}
                                    onChange={(value) => setRetryCount(typeof value === "number" ? value : 5)}
                                    style={{ width: "100%" }}
                                />
                            </div>
                            <div className="_f _fm" style={{ gap: 8, minHeight: 32, paddingBottom: 2 }}>
                                <Switch
                                    checked={onlyFailed}
                                    onChange={(checked) => {
                                        void loadInspection(checked);
                                    }}
                                />
                                <span>{labels.onlyFailed}</span>
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                alignItems: "center",
                            }}
                        >
                            <ButtonH
                                sm
                                ghost
                                onClick={() => {
                                    void loadInspection(false);
                                }}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                <Ico
                                    size={"16"}
                                    style={
                                        inspectionLoading
                                            ? {
                                                  animation: "voiceoverV2Spin 1s linear infinite",
                                                  display: "inline-block",
                                              }
                                            : undefined
                                    }
                                >
                                    refresh
                                </Ico>
                                {labels.inspectCurrentFolder}
                            </ButtonH>
                            <ButtonH
                                sm
                                ghost
                                onClick={() => {
                                    void loadInspection(true);
                                }}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                <Ico size={"16"}>report</Ico>
                                {labels.inspectFailed}
                            </ButtonH>
                            <AutoEditGate
                                blocked={autoEdit.blocked && voiceoverV2FolderRunEditsVoicing(mode)}
                                reason={autoEdit.reason}
                            >
                                <ButtonH
                                    sm
                                    ghost
                                    onClick={() => {
                                        if (runLoading) {
                                            return;
                                        }
                                        void handleRunFolder();
                                    }}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <Ico
                                        size={"16"}
                                        style={
                                            runLoading === "folder"
                                                ? {
                                                      animation: "voiceoverV2Spin 1s linear infinite",
                                                      display: "inline-block",
                                                  }
                                                : undefined
                                        }
                                    >
                                        folder
                                    </Ico>
                                    {labels.runCurrentFolder}
                                </ButtonH>
                            </AutoEditGate>
                            <AutoEditGate {...autoEdit}>
                                <ButtonH
                                    sm
                                    ghost
                                    onClick={handleRunFolderWithRegenerate}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <Ico size={"16"}>warning</Ico>
                                    {labels.runFolderWithRegenerate}
                                </ButtonH>
                            </AutoEditGate>
                            {runLoading === "folder" && (
                                <ButtonH
                                    sm
                                    ghost
                                    red
                                    onClick={handleStopFolderRun}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <Ico size={"16"}>stop</Ico>
                                    {folderProgress?.stopping ? labels.folderStopping : labels.folderStop}
                                </ButtonH>
                            )}
                        </div>
                    </div>

                    <div className="_f _fm" style={{ gap: 8 }}>
                        <Tooltip title={selectedFragmentTooltip}>
                            <span>
                                <ButtonH
                                    sm
                                    ghost
                                    disabled={!selectedFragmentId}
                                    onClick={() => {
                                        if (runLoading || !selectedFragmentId) {
                                            return;
                                        }
                                        void handleRunFragment(selectedFragmentId);
                                    }}
                                >
                                    <Ico
                                        size={"16"}
                                        style={
                                            runLoading === "fragment"
                                                ? {
                                                      animation: "voiceoverV2Spin 1s linear infinite",
                                                      display: "inline-block",
                                                  }
                                                : undefined
                                        }
                                    >
                                        play_arrow
                                    </Ico>
                                    {labels.runSelectedFragment}
                                </ButtonH>
                            </span>
                        </Tooltip>
                        <Tag color={selectedFragmentId ? "blue" : "default"}>
                            {selectedFragmentId
                                ? labels.selectedFragment(selectedFragmentId)
                                : labels.noFragmentSelected}
                        </Tag>
                    </div>

                    {folderProgress && (
                        <div
                            style={{
                                padding: 12,
                                backgroundColor: "#fafafa",
                                border: "1px solid #e8e8e8",
                                borderRadius: 6,
                            }}
                        >
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <strong>{labels.folderProgress}</strong>
                                <Tag>{folderProgress.processed}/{folderProgress.total}</Tag>
                                <Tag color="green">{labels.passed}: {folderProgress.passed}</Tag>
                                <Tag color="orange">{labels.warnings}: {folderProgress.warnings}</Tag>
                                <Tag color="red">{labels.errors}: {folderProgress.errors}</Tag>
                                <Tag>
                                    {folderProgress.stopping
                                        ? labels.folderStopping
                                        : folderProgress.total > 0 &&
                                            folderProgress.processed >= folderProgress.total
                                          ? labels.folderCompleted
                                        : folderProgress.currentFragmentId
                                          ? labels.folderProcessing
                                          : labels.folderPreparing}
                                </Tag>
                            </div>
                            <Progress
                                percent={folderProgressPercent}
                                status={
                                    folderProgress.stopped
                                        ? "exception"
                                        : folderProgress.total > 0 &&
                                            folderProgress.processed >= folderProgress.total
                                          ? "success"
                                          : "active"
                                }
                                style={{ marginTop: 8 }}
                            />
                            {folderProgress.currentFragmentId ? (
                                <div style={{ marginTop: 8, color: "#555" }}>
                                    {labels.folderCurrentFragment}: {folderProgress.currentFragmentId}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {lastRun?.type === "fragment" && (
                        <div
                            style={{
                                padding: 12,
                                backgroundColor: "#fafafa",
                                border: "1px solid #e8e8e8",
                                borderRadius: 6,
                            }}
                        >
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <strong>{labels.lastFragmentRun}</strong>
                                <Tag color={lastRun.response.passed ? "green" : "orange"}>
                                    {formatVoiceoverV2Status(lastRun.response.status, labels)}
                                </Tag>
                                <Tag>{lastRun.fragmentId}</Tag>
                                {lastRun.response.message ? <span>{lastRun.response.message}</span> : null}
                            </div>
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <Tag color={lastRun.response.passed ? "green" : "red"}>
                                    {labels.passed}: {lastRun.response.passed ? labels.yes : labels.no}
                                </Tag>
                                <Tag>
                                    {labels.quality}:{" "}
                                    {getCheckSummary(lastRun.response.quality_check || undefined, labels)}
                                </Tag>
                                <Tag>
                                    {labels.intonation}:{" "}
                                    {getCheckSummary(
                                        lastRun.response.intonation_drop_check || undefined,
                                        labels,
                                    )}
                                </Tag>
                            </div>
                            {lastRun.response.last_error ? (
                                <div style={{ marginTop: 8, color: "#cf1322" }}>
                                    {lastRun.response.last_error}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {lastRun?.type === "folder" && folderSummary && (
                        <div
                            style={{
                                padding: 12,
                                backgroundColor: "#fafafa",
                                border: "1px solid #e8e8e8",
                                borderRadius: 6,
                            }}
                        >
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <strong>{labels.lastFolderRun}</strong>
                                <Tag>{labels.total}: {folderSummary.total}</Tag>
                                <Tag color="green">{labels.passed}: {folderSummary.passed}</Tag>
                                <Tag color="orange">{labels.warnings}: {folderSummary.warnings}</Tag>
                                <Tag color="red">{labels.errors}: {folderSummary.errors}</Tag>
                            </div>
                            {folderSummary.problematic.length > 0 ? (
                                <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {folderSummary.problematic.map((item: VoiceoverV2FolderResultItem) => (
                                        <Tag key={item.id} color={item.result.status === "error" ? "red" : "orange"}>
                                            {item.id}: {formatVoiceoverV2Status(item.result.status, labels)}
                                        </Tag>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    )}

                    <Table
                        dataSource={inspectionRows}
                        columns={columns}
                        rowKey={(record) => `${record.folder_num}_${record.chunk_id}`}
                        size="small"
                        pagination={false}
                        loading={inspectionLoading}
                        scroll={{ y: 360 }}
                        locale={{
                            emptyText: onlyFailed
                                ? labels.noFailedData
                                : labels.noInspectionData,
                        }}
                    />
                </div>
            </Modal>
        </>
    );
};
